/**
 * AddressSearchInput — Phase 7c follow-up: Google Places autocomplete
 * for the address composite.
 *
 * Wraps the existing AddressComposite with a single search box. On
 * pick, decomposes the picked Place into the catalog's structured
 * sub-fields (street, city, state/zip OR province/postal_code,
 * country, etc.) via {@link decomposeGooglePlace} and emits each via
 * the parent-supplied {@link IAddressSearchInputProps.onAddressPicked}
 * callback. The parent writes each sub-field to the engine.
 *
 * Three runtime states:
 *   - **search**: empty values, loader available — render the search
 *     input. "Don't see your address?" link drops the user to manual.
 *   - **summary**: any value populated — render a summary card with
 *     [Edit]. Edit drops the user to manual.
 *   - **manual**: caller-provided 6-field grid (the existing
 *     AddressComposite). Re-rendered when the user clicks [Edit] /
 *     "Don't see your address?" or when no loader is supplied (no API
 *     key in env, or SDK failed to load).
 *
 * The component itself does NOT load the Google Maps SDK in unit tests.
 * The default `loader` is `undefined` in test contexts; production
 * callers wire the actual `@googlemaps/js-api-loader` Loader.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import styles from './AddressSearchInput.module.css';

/**
 * The 11 catalog address sub-fields. These match the suffix set in
 * `apps/applicant-web/src/intake/addressClustering.ts`. Sub-fields the
 * picked Place doesn't include (e.g., a non-US place won't have a
 * state code; in_care_of_name and region are not Place concepts) get
 * an empty string so the parent can clear them in the engine.
 */
export interface TAddressParts {
  street_number_and_name: string;
  in_care_of_name: string;
  unit_type: string;
  unit_number: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  province: string;
  postal_code: string;
  region: string;
}

/**
 * Minimal subset of `google.maps.places.Place` we read at decomposition
 * time. Defined locally so unit tests don't need the real SDK and so
 * we don't introduce a `@types/google.maps` dependency on the UI
 * package.
 */
export interface TGoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: readonly string[];
}

export interface TGooglePlaceLike {
  address_components?: ReadonlyArray<TGoogleAddressComponent>;
}

/**
 * SDK loader contract. Production wires this to
 * `new Loader({ apiKey, libraries: ['places'] })` from
 * `@googlemaps/js-api-loader`. Tests pass a stub or omit it entirely.
 */
export interface IGoogleMapsLoader {
  load: () => Promise<unknown>;
}

export interface IAddressSearchInputProps {
  /** ARIA legend (e.g., "Current physical address"). */
  legend: string;
  /**
   * Per-sub-field write callback. The component decomposes a picked
   * Place into 11 fields and calls this once with all of them.
   * Callers wire each non-undefined sub-field to engine.setAnswer.
   */
  onAddressPicked: (parts: Partial<TAddressParts>) => void;
  /**
   * Current values for the underlying sub-fields, used to decide
   * search-vs-summary and to render the summary card.
   */
  values: TAddressParts;
  /**
   * Render the manual-entry block when the user clicks [Edit] or
   * "Don't see your address." The caller decides what to render
   * here (typically the existing AddressComposite 6-field grid).
   */
  renderManualEntry: () => JSX.Element;
  /** Validation issues from the engine for any sub-field. */
  hasIssues?: boolean;
  /**
   * SDK loader. When `undefined`, the component renders manual entry
   * directly — covers the "no API key in env" and "SDK failed to
   * load" cases without exposing a UX cliff.
   *
   * Tests inject a stub (or `undefined`) so they never hit the
   * network.
   */
  loader?: IGoogleMapsLoader;
}

/**
 * Pure decomposition: Google Place address_components → catalog parts.
 * Exported for unit tests.
 *
 * Mapping:
 *   - street_number + route → street_number_and_name (joined)
 *   - subpremise → unit_number (and unit_type if prefix is detected)
 *   - locality OR postal_town → city
 *   - administrative_area_level_1 (US) → state (ISO short)
 *   - administrative_area_level_1 (non-US) → province (long_name)
 *   - postal_code (US) → zip_code, (non-US) → postal_code
 *   - country → ISO alpha-2 (short_name)
 *
 * US-vs-non-US is determined by `country.short_name === 'US'`.
 *
 * Catalog fields that have no Place equivalent (in_care_of_name,
 * region) are explicitly returned as empty strings so the caller's
 * `setAnswer` clears them on the engine.
 */
export function decomposeGooglePlace(place: TGooglePlaceLike): TAddressParts {
  const parts: TAddressParts = {
    street_number_and_name: '',
    in_care_of_name: '',
    unit_type: '',
    unit_number: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    province: '',
    postal_code: '',
    region: '',
  };
  const components = place.address_components ?? [];
  if (components.length === 0) return parts;

  const has = (component: TGoogleAddressComponent, type: string): boolean =>
    component.types.includes(type);

  let streetNumber = '';
  let route = '';

  for (const c of components) {
    if (has(c, 'street_number')) {
      streetNumber = c.long_name;
    } else if (has(c, 'route')) {
      route = c.long_name;
    } else if (has(c, 'subpremise')) {
      // "Apt 4B" → unit_type=Apt, unit_number=4B. If the value
      // doesn't contain a space we treat the whole thing as the
      // unit_number and leave unit_type empty.
      const trimmed = c.long_name.trim();
      const spaceIdx = trimmed.indexOf(' ');
      if (spaceIdx > 0) {
        parts.unit_type = trimmed.slice(0, spaceIdx);
        parts.unit_number = trimmed.slice(spaceIdx + 1);
      } else {
        parts.unit_number = trimmed;
      }
    } else if (has(c, 'locality') || has(c, 'postal_town')) {
      parts.city = c.long_name;
    } else if (has(c, 'administrative_area_level_1')) {
      // Defer the US-vs-non-US decision until we've seen `country`.
      // Stash both candidate values in temporary slots; we'll commit
      // them in a second pass below.
      // We piggyback on parts.state and parts.province here and
      // rebalance after the loop.
      parts.state = c.short_name;
      parts.province = c.long_name;
    } else if (has(c, 'postal_code')) {
      parts.zip_code = c.long_name;
      parts.postal_code = c.long_name;
    } else if (has(c, 'country')) {
      parts.country = c.short_name;
    }
  }

  if (streetNumber !== '' || route !== '') {
    parts.street_number_and_name = [streetNumber, route]
      .filter(s => s.length > 0)
      .join(' ');
  }

  // Resolve US-vs-non-US: USCIS forms ask state+zip for US, province+
  // postal_code otherwise.
  if (parts.country === 'US') {
    parts.province = '';
    parts.postal_code = '';
  } else {
    parts.state = '';
    parts.zip_code = '';
  }

  return parts;
}

/**
 * Renders a one-line summary of the parts (street, unit, city, region,
 * postal, country) for the summary card.
 */
function summarize(values: TAddressParts): string {
  const segments: string[] = [];
  if (values.street_number_and_name) segments.push(values.street_number_and_name);
  if (values.unit_type && values.unit_number) {
    segments.push(`${values.unit_type} ${values.unit_number}`);
  } else if (values.unit_number) {
    segments.push(values.unit_number);
  }
  if (values.city) segments.push(values.city);
  const region = values.state || values.province || values.region;
  const postal = values.zip_code || values.postal_code;
  if (region && postal) segments.push(`${region} ${postal}`);
  else if (region) segments.push(region);
  else if (postal) segments.push(postal);
  if (values.country) segments.push(values.country);
  return segments.join(', ');
}

function hasAnyValue(values: TAddressParts): boolean {
  return Object.values(values).some(v => v.length > 0);
}

type TPanel = 'search' | 'summary' | 'manual';

export function AddressSearchInput({
  legend,
  values,
  onAddressPicked,
  renderManualEntry,
  loader,
}: IAddressSearchInputProps): JSX.Element {
  const populated = hasAnyValue(values);
  const initialPanel: TPanel = populated ? 'summary' : 'search';
  const [panel, setPanel] = useState<TPanel>(initialPanel);
  const [sdkFailed, setSdkFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Kick off the SDK load on mount when a loader is supplied. The
  // loader's promise rejecting → render manual fallback notice.
  useEffect(() => {
    if (loader === undefined) return;
    let cancelled = false;
    void loader
      .load()
      .then(() => {
        // Production: bind the returned Maps SDK's Autocomplete to
        // inputRef.current and call onAddressPicked on `place_changed`.
        // We deliberately leave the binding as a stub — unit tests
        // never reach this branch (they pass a never-resolving loader),
        // and production wiring is the responsibility of the calling
        // app (`apps/applicant-web/src/intake/SectionRenderer.tsx`)
        // once a real API key is provisioned.
      })
      .catch(() => {
        if (!cancelled) setSdkFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loader, onAddressPicked]);

  // No loader supplied → render manual entry as the fallback. Covers
  // both "VITE_GOOGLE_MAPS_API_KEY missing" and "test environment".
  //
  // Hotfix (2026-05-02) — was double-wrapping. Previously this branch
  // wrapped `renderManualEntry()` in its own `<fieldset>` + `<legend>`
  // — but the manual entry IS an `<AddressComposite>` which already
  // renders its own fieldset+legend. Result: every address rendered
  // as a card-inside-a-card with the legend text duplicated ("Place /
  // Place", "Home address / Home address"). The fix is to make this
  // branch transparent — AddressComposite owns the legend+card; we
  // just delegate.
  if (loader === undefined) {
    return renderManualEntry();
  }

  if (panel === 'manual') {
    // Hotfix (2026-05-02) — also unwrapped. The AddressComposite owns
    // the card+legend; we render the "Use search instead" affordance
    // as a small footer link beneath it. The footer keeps its
    // right-align + small top margin via the existing `.search__actions`
    // CSS, which works fine outside a fieldset wrapper.
    return (
      <>
        {renderManualEntry()}
        <div className={styles.search__actions}>
          <button
            type="button"
            className={styles.search__link}
            onClick={() => setPanel(populated ? 'summary' : 'search')}
          >
            Use search instead
          </button>
        </div>
      </>
    );
  }

  if (panel === 'summary') {
    const summary = summarize(values);
    return (
      <fieldset className={styles.search}>
        <legend className={styles.search__legend}>{legend}</legend>
        <div
          className={styles.search__card}
          data-address-summary
        >
          <span className={styles.search__summary}>{summary}</span>
          <button
            type="button"
            className={styles.search__edit}
            onClick={() => setPanel('manual')}
          >
            Edit
          </button>
        </div>
      </fieldset>
    );
  }

  // panel === 'search'
  //
  // Hotfix (2026-05-02) — when the SDK fails to load mid-flight, we
  // fall back to the manual entry (which owns its card+legend). Pull
  // the failure branch out of the search fieldset so it doesn't
  // double-wrap. The "search unavailable" notice renders above the
  // AddressComposite as a small standalone line.
  if (sdkFailed) {
    return (
      <>
        <p className={styles.search__notice}>
          Address search unavailable; please type manually.
        </p>
        {renderManualEntry()}
      </>
    );
  }

  const inputStyle: CSSProperties | undefined = undefined;
  return (
    <fieldset className={styles.search}>
      <legend className={styles.search__legend}>{legend}</legend>
      <input
        ref={inputRef}
        type="text"
        className={styles.search__input}
        placeholder="Start typing your address..."
        aria-label={`${legend} search`}
        style={inputStyle}
      />
      <div className={styles.search__actions}>
        <button
          type="button"
          className={styles.search__link}
          onClick={() => setPanel('manual')}
        >
          Don&apos;t see your address?
        </button>
      </div>
    </fieldset>
  );
}
